/**
 * The brand's type-on (BRAND_SPEC §9.1, the engine in
 * campaign/brand/samples/src/typeon.js), for the welcome's "Ask anything.":
 * the first word is already there at frame 0 (never an empty heading), the
 * rest types one character at a time with a jitter seeded from the words, so
 * it types the same way on every visit, a beat longer after a space, and 200 ms
 * after the last character the caret drops to its "your turn" place: the start
 * of the empty box (ChatView).
 *
 * The pace is the engine's, slowed from 60 to 120 ms a character so the ten
 * characters after "Ask" take about 1.3 s: slow enough for anyone, an
 * 84-year-old on a first visit included, to see it being typed rather than
 * take it for the page still loading (the caret is in the box by about 1.5 s).
 */
export const TYPE_ON = {
  char: 120, // per character (§9.1 says 60 for the ads; slower here, see above)
  jitter: 15, // ± ms, seeded from the words
  space: 90, // added after a space
  comma: 260, // added after , ; :
  turnDrop: 200, // the caret stays after the last character this long, then drops
};

// FNV-1a and mulberry32, exactly as typeon.js, so the same words get the same rhythm.
function hash(s) {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function seededRandom(seed) {
  let s = seed;
  return () => {
    s |= 0;
    s = (s + 0x6D2B79F5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * The typing as steps: from `at` ms, the first `shown` characters are on
 * screen. The first step is frame 0 (the first word). `typedAt` is when the
 * last character lands; `settleAt` when the caret drops to its your-turn place.
 */
export function typeOnSteps(text, pace = TYPE_ON) {
  const full = String(text || '');
  const first = full.split(' ')[0].length;
  const rnd = seededRandom(hash(full));
  const steps = [{ at: 0, shown: first }];
  let t = 0;
  for (let k = first; k < full.length; k++) {
    const prev = k > 0 ? full.charAt(k - 1) : '';
    t += pace.char + (rnd() * 2 - 1) * pace.jitter;
    if (prev === ' ') t += pace.space;
    if (prev === ',' || prev === ';' || prev === ':') t += pace.comma;
    steps.push({ at: Math.round(t), shown: k + 1 });
  }
  return { steps, typedAt: Math.round(t), settleAt: Math.round(t + pace.turnDrop) };
}

/** How many characters are on screen `ms` after the typing started. */
export function shownAt(steps, ms) {
  let n = steps[0].shown;
  for (let i = 1; i < steps.length && steps[i].at <= ms; i++) n = steps[i].shown;
  return n;
}

// Once a visit (BRAND_SPEC §9.6): a reload, or the welcome coming back for a
// new conversation, shows the words at once.
const TYPED_KEY = 'apr-welcome-typed';

export function typedThisVisit() {
  try {
    return sessionStorage.getItem(TYPED_KEY) === '1';
  } catch (e) {
    return false;
  }
}

export function markTyped() {
  try {
    sessionStorage.setItem(TYPED_KEY, '1');
  } catch (e) { /* storage blocked: it types again next time, which is harmless */ }
}
